import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, Ruler, FileDown, Sheet, ClipboardPlus, Mail, TriangleAlert, Monitor, Plus } from 'lucide-react';
import MfrPicker from './MfrPicker';
import { useMfrs, downloadMfr, getMfr } from '../lib/mfr';
import { MFR_ID, bomPdf, bomXlsx, quoteMail } from '../lib/firePond';
import { PLANS, planDefaults, planBom } from '../lib/sitePlans';

export const openPond = (id) => { const e = new Event('giss-pond-open'); e.plan = typeof id === 'string' ? id : null; window.dispatchEvent(e); };
const lab = (f, t) => (typeof f.label === 'function' ? f.label(t) : f.label);

function Field({ t, f, p, set }) {
  const id = `fp-${f.k}`;
  if (f.type === 'chk') return <label className="fp-chk"><input type="checkbox" data-testid={id} checked={!!p[f.k]} onChange={(e) => set({ [f.k]: e.target.checked })} /> {lab(f, t)}</label>;
  if (f.type === 'sel') {
    const opts = typeof f.opts === 'function' ? f.opts(t) : f.opts;
    return <label>{lab(f, t)}<select data-testid={id} value={p[f.k]} onChange={(e) => set({ [f.k]: e.target.value })}>{opts.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>;
  }
  return <label>{lab(f, t)}<input inputMode="decimal" data-testid={id} value={p[f.k] ?? ''} onChange={(e) => set({ [f.k]: e.target.value.replace(',', '.') })} /></label>;
}

function Kpis({ t, pl, c }) {
  const f = (v, d = 0) => v.toLocaleString(t.locale, { maximumFractionDigits: d });
  return (
    <div className="fp-kpis" data-testid="fp-result">
      {pl.kpis(t, c, f).map(([k, v, id]) => <div key={id} className="fp-kpi" data-testid={`fp-kpi-${id}`}><b>{v}</b><small>{k}</small></div>)}
      {c.warn.map((w) => <div key={w} className="fp-warn" data-testid={`fp-warn-${w}`}><TriangleAlert size={14} /> {t[w]}</div>)}
    </div>
  );
}

function Bom({ t, rows, setQty }) {
  return (
    <div className="fp-bom-wrap">
      <table className="mat-table fp-bom" data-testid="fp-bom">
        <thead><tr><th>{t.fpPos}</th><th>{t.mfColName}</th><th>{t.rmQty}</th><th>{t.mfColUnit}</th><th>{t.mfColDim}</th><th>{t.mfColMat}</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.ref}-${r.pos}`} data-testid={`fp-bom-row-${r.pos}`}>
            <td>{r.pos}</td>
            <td>{r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.name}</a> : r.name}<small>{r.other ? ` · ${t.fpOther}` : ` · ${r.mfr}`}</small></td>
            <td><input className="rm-num" inputMode="decimal" data-testid={`fp-bom-qty-${r.pos}`} key={r.qty} defaultValue={r.qty.toLocaleString(t.locale)} onBlur={(e) => setQty(r, e.target.value)} /></td>
            <td>{t['unit_' + r.unit] || r.unit}</td><td>{r.dim}</td><td>{r.mat}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

export default function SitePlanner({ t, wide, project, report, updateReport }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState('pond');
  const [vals, setVals] = useState({});
  const [ov, setOv] = useState({});
  const [extraM, setExtraM] = useState({});
  const [picker, setPicker] = useState(false);
  const mf = useMfrs(), doc = mf[MFR_ID];
  useEffect(() => { const on = (e) => { if (e.plan) setPlan(e.plan); setOpen(true); }; window.addEventListener('giss-pond-open', on); return () => window.removeEventListener('giss-pond-open', on); }, []);
  // refresh the catalogue in the background (own prices are kept)
  useEffect(() => { if (open && wide) downloadMfr(MFR_ID).catch(() => !getMfr(MFR_ID) && toast.error(t.fpNoCat)); }, [open, wide, t]);
  if (!open) return null;
  const pl = PLANS.find((x) => x.id === plan) || PLANS[0];
  const p = { ...planDefaults(pl), title: project?.title || '', ...vals[plan] };
  const set = (patch) => { setVals((v) => ({ ...v, [plan]: { ...v[plan], ...patch } })); setOv((o) => Object.fromEntries(Object.entries(o).filter(([k]) => !k.startsWith(`${plan}|`)))); };
  const ovKey = (r) => `${plan}|${r.ref}|${r.unit}`;
  const extra = extraM[plan] || [];
  const base = planBom(t, pl, p, doc, extra);
  const rows = base.rows.map((r) => ({ ...r, qty: ov[ovKey(r)] ?? r.qty })).filter((r) => r.qty > 0).map((r, i) => ({ ...r, pos: i + 1 }));
  const res = { c: base.c, rows }, title = p.title || pl.name(t);
  const setQty = (r, v) => { if (String(v).trim() === '') return; const sv = String(v).trim(), q = +(sv.includes(',') ? sv.replace(/\./g, '').replace(',', '.') : sv); if (Number.isFinite(q)) setOv((o) => ({ ...o, [ovKey(r)]: q })); };
  const addItem = (x) => { setExtraM((m) => ({ ...m, [plan]: [...(m[plan] || []), { ref: `${x.mfrId}:${x.id}:${Date.now().toString(36)}`, mfrId: x.mfrId, mfr: x.mfrId ? x.mfr : '', name: x.name, dim: x.dim || '', mat: x.mat || '', sku: x.sku || '', unit: x.unit || 'pcs', qty: 1, price: x.price ?? null, url: x.url || '', other: !x.mfrId, free: true }] })); toast.success(t.rmAdded); };
  const take = () => {
    if (!report) return toast.error(t.fpNoReport);
    const add = rows.map((r) => ({ id: `rm${Date.now().toString(36)}${r.pos}`, ref: r.ref, mfrId: r.mfrId, mfr: r.mfr, name: r.name, dim: r.dim, sku: r.sku, mat: r.mat, unit: r.unit, qty: r.qty, price: r.price, url: r.url }));
    updateReport({ materials: [...(report.materials || []), ...add] });
    toast.success(t.fpTaken.replace('{n}', add.length));
  };
  const mfrOf = rows.find((r) => r.mfrId) ? getMfr(rows.find((r) => r.mfrId).mfrId) : doc;
  return createPortal(
    <div className="editor-backdrop" data-testid="fp-dialog">
      <div className="editor fp-dialog">
        <div className="editor-head">
          <h4><Ruler size={18} /> {t.fpOpen}</h4>
          <button className="icon-btn" data-testid="fp-close-btn" onClick={() => setOpen(false)}><X size={16} /></button>
        </div>
        {!wide ? <div className="empty" data-testid="fp-desktop-only"><Monitor size={16} /> {t.fpDesktop}</div> : !doc ? <div className="empty" data-testid="fp-loading">{t.fpNeedCat}</div> : (<>
          <div className="fp-tpls" data-testid="fp-templates">
            <b>{t.fpTpl}:</b>
            {PLANS.map((x) => <button key={x.id} className={`chip seg ${plan === x.id ? 'on' : ''}`} data-testid={`fp-tpl-${x.id}`} onClick={() => { setPlan(x.id); setPicker(x.id === 'free'); }}>{x.icon} {x.name(t)}</button>)}
          </div>
          <div className="fp-split">
            <div className="fp-form" data-testid="fp-form">
              <label>{t.fpProject}<input data-testid="fp-title" value={p.title} onChange={(e) => set({ title: e.target.value })} /></label>
              {pl.fields.map((f) => <Field key={f.k} t={t} f={f} p={p} set={set} />)}
              {plan === 'free' && <p className="hint">{t.fpFreeHint}</p>}
            </div>
            <div className="fp-out">
              {pl.fields.length > 0 && <><h5>{t.fpResult}</h5><Kpis t={t} pl={pl} c={base.c} /></>}
              <h5>{t.fpBom} – {pl.name(t)}</h5>
              {rows.length ? <Bom t={t} rows={rows} setQty={setQty} /> : <div className="empty" data-testid="fp-bom-empty">{t.fpFreeHint}</div>}
              <button className="btn btn-outline btn-sm fp-add" data-testid="fp-add-item-btn" onClick={() => setPicker(!picker)}><Plus size={14} /> {t.rmPick}</button>
              {picker && <MfrPicker t={t} testid="fp-picker" defSector="all" onPick={addItem} onOwn={(q) => addItem({ id: `own-${q}`, mfrId: '', name: q })} />}
              <p className="hint">{t.fpNoPrice}</p>
              <div className="row fp-actions">
                <button className="btn btn-dark btn-sm" disabled={!rows.length} data-testid="fp-pdf-btn" onClick={() => bomPdf(t, title, pl, p, res, mfrOf)}><FileDown size={14} /> {t.fpPdf}</button>
                <button className="btn btn-outline btn-sm" disabled={!rows.length} data-testid="fp-xlsx-btn" onClick={() => bomXlsx(t, title, rows)}><Sheet size={14} /> {t.fpXlsx}</button>
                <button className="btn btn-green btn-sm" disabled={!rows.length} data-testid="fp-report-btn" onClick={take}><ClipboardPlus size={14} /> {t.fpToReport}</button>
                <button className="btn btn-proj btn-sm" disabled={!rows.length} data-testid="fp-quote-btn" title={t.rmQuoteHint} onClick={() => quoteMail(t, mfrOf, rows, title, `${t.fpMailSubj2} ${pl.name(t)}`)}><Mail size={14} /> {t.rmQuote}</button>
              </div>
              <p className="hint">{t.fpNote}</p>
            </div>
          </div>
        </>)}
      </div>
    </div>,
    document.body,
  );
}
